import { AdminTable } from './shared/AdminDataGrid';
/**
 * Notification Translation Management Component
 * Allows admins to view and manage notification translations for all notification types
 */

import { useState, useEffect } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Box,
  Typography,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField as MuiTextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { adminApi } from '../providers/dataProvider';
import { logger } from '../utils/logger';
import { LANGUAGE_LABELS } from '../constants';

interface NotificationTranslation {
  language: string;
  titleTemplate: string;
  messageTemplate: string;
}

interface NotificationTypeDetail {
  readOnly?: boolean;
  type: string;
  variables: string[];
  translations: NotificationTranslation[];
}

export function NotificationTranslationManagement() {
  const notify = useNotify();

  // State
  const [notificationTypes, setNotificationTypes] = useState<NotificationTypeDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [editingTranslation, setEditingTranslation] = useState<NotificationTranslation | null>(null);
  const [titleTemplate, setTitleTemplate] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [saving, setSaving] = useState(false);

  /**
   * Load all notification types with translations
   */
  const loadNotificationTypes = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getNotificationTranslations();
      setNotificationTypes(response.data);
    } catch (error) {
      notify('Failed to load notification translations', { type: 'error' });
      logger.error('Failed to load notification translations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotificationTypes();
  }, []);

  /**
   * Get available languages for a notification type
   */
  const getAvailableLanguages = (type: NotificationTypeDetail) => {
    const existingLanguages = type.translations.map((t) => t.language);
    const allLanguages = ['en', 'fr', 'es', 'de', 'it'];
    return allLanguages.filter((lang) => !existingLanguages.includes(lang));
  };

  /**
   * Get English translation for reference
   */
  const getEnglishTranslation = (type: NotificationTypeDetail) => {
    return type.translations.find((t) => t.language === 'en');
  };

  /**
   * Open edit dialog
   */
  const handleOpenEditDialog = (
    type: string,
    translation?: NotificationTranslation,
    language?: string
  ) => {
    setSelectedType(type);
    if (translation) {
      setEditingTranslation(translation);
      setSelectedLanguage(translation.language);
      setTitleTemplate(translation.titleTemplate);
      setMessageTemplate(translation.messageTemplate);
    } else {
      // Adding new translation
      setEditingTranslation(null);
      setSelectedLanguage(language || '');
      setTitleTemplate('');
      setMessageTemplate('');
    }
    setEditDialogOpen(true);
  };

  /**
   * Close edit dialog
   */
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedType(null);
    setSelectedLanguage('');
    setEditingTranslation(null);
    setTitleTemplate('');
    setMessageTemplate('');
  };

  /**
   * Save translation
   */
  const handleSaveTranslation = async () => {
    if (!selectedType || !selectedLanguage) {
      notify('Type and language are required', { type: 'warning' });
      return;
    }

    if (!titleTemplate.trim() || !messageTemplate.trim()) {
      notify('Title and message templates are required', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await adminApi.upsertNotificationTranslation(
        selectedType,
        selectedLanguage,
        titleTemplate,
        messageTemplate
      );

      notify(
        editingTranslation
          ? 'Translation updated successfully'
          : 'Translation added successfully',
        { type: 'success' }
      );

      handleCloseEditDialog();
      loadNotificationTypes();
    } catch (error) {
      notify('Failed to save translation', { type: 'error' });
      logger.error('Failed to save translation:', error);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Open delete dialog
   */
  const handleOpenDeleteDialog = (type: string, translation: NotificationTranslation) => {
    setSelectedType(type);
    setEditingTranslation(translation);
    setDeleteDialogOpen(true);
  };

  /**
   * Close delete dialog
   */
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedType(null);
    setEditingTranslation(null);
  };

  /**
   * Delete translation
   */
  const handleDeleteTranslation = async () => {
    if (!selectedType || !editingTranslation) {
      return;
    }

    setSaving(true);
    try {
      await adminApi.deleteNotificationTranslation(selectedType, editingTranslation.language);
      notify('Translation deleted successfully', { type: 'success' });
      handleCloseDeleteDialog();
      loadNotificationTypes();
    } catch (error) {
      notify('Failed to delete translation', { type: 'error' });
      logger.error('Failed to delete translation:', error);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Format notification type for display
   */
  const formatNotificationType = (type: string): string => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Title title="Notification Translations" />

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Manage notification templates across all languages. Templates use {'{{'} {'}}'}  syntax for
        variable interpolation.
      </Typography>

      <Box sx={{ mt: 3 }}>
        {notificationTypes.map((type) => {
          const englishTranslation = getEnglishTranslation(type);
          const availableLanguages = getAvailableLanguages(type);

          return (
            <Accordion key={type.type} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {formatNotificationType(type.type)}
                  </Typography>
                  <Chip
                    label={`${type.translations.length} language${type.translations.length !== 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                  />
                  {type.variables.length > 0 && (
                    <Chip
                      label={`${type.variables.length} variable${type.variables.length !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {type.readOnly && <Alert severity="info" sx={{ mb: 2 }}>
                    These reminders use reviewed wording in all supported languages. This preview shows four remaining questions; singular wording is handled automatically.
                  </Alert>}
                  {type.variables.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Available Variables:
                      </Typography>
                      <Typography variant="body2">
                        {type.variables.map((v) => `{{${v}}}`).join(', ')}
                      </Typography>
                    </Alert>
                  )}

                  {englishTranslation && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight="bold">
                        English Template (Reference):
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Title:</strong> {englishTranslation.titleTemplate}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Message:</strong> {englishTranslation.messageTemplate}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1">Translations</Typography>
                    {!type.readOnly && availableLanguages.length > 0 && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenEditDialog(type.type, undefined, availableLanguages[0])}
                      >
                        Add Translation
                      </Button>
                    )}
                  </Box>

                  <TableContainer component={Paper} variant="outlined">
                    <AdminTable size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={120}>Language</TableCell>
                          <TableCell>Title Template</TableCell>
                          <TableCell>Message Template</TableCell>
                          <TableCell width={100} align="right">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {type.translations
                          .filter((t) => t.language !== 'en')
                          .map((translation) => (
                            <TableRow key={translation.language}>
                              <TableCell>
                                <Chip
                                  label={LANGUAGE_LABELS[translation.language] || translation.language}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{translation.titleTemplate}</TableCell>
                              <TableCell>{translation.messageTemplate}</TableCell>
                              <TableCell align="right">
                                {!type.readOnly && <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEditDialog(type.type, translation)}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteDialog(type.type, translation)}
                                  title="Delete"
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                                </>}
                              </TableCell>
                            </TableRow>
                          ))}
                        {type.translations.filter((t) => t.language !== 'en').length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="textSecondary">
                                No translations available. Click "Add Translation" to add one.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </AdminTable>
                  </TableContainer>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Edit/Add Translation Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTranslation
            ? `Edit ${LANGUAGE_LABELS[editingTranslation.language]} Translation`
            : 'Add Translation'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {!editingTranslation && (
              <MuiTextField
                select
                label="Language"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                {selectedType &&
                  getAvailableLanguages(
                    notificationTypes.find((t) => t.type === selectedType)!
                  ).map((lang) => (
                    <option key={lang} value={lang}>
                      {LANGUAGE_LABELS[lang] || lang}
                    </option>
                  ))}
              </MuiTextField>
            )}

            {selectedType && (
              <>
                {(() => {
                  const type = notificationTypes.find((t) => t.type === selectedType);
                  const englishTranslation = type ? getEnglishTranslation(type) : null;

                  return (
                    <>
                      {type && type.variables.length > 0 && (
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Available Variables:</strong>{' '}
                            {type.variables.map((v) => `{{${v}}}`).join(', ')}
                          </Typography>
                        </Alert>
                      )}

                      {englishTranslation && (
                        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Typography variant="caption" color="textSecondary" fontWeight="bold">
                            English (Reference):
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>Title:</strong> {englishTranslation.titleTemplate}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>Message:</strong> {englishTranslation.messageTemplate}
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </>
            )}

            <MuiTextField
              label="Title Template"
              value={titleTemplate}
              onChange={(e) => setTitleTemplate(e.target.value)}
              fullWidth
              required
              helperText="Use {{variableName}} for interpolation"
            />

            <MuiTextField
              label="Message Template"
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
              helperText="Use {{variableName}} for interpolation"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSaveTranslation} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Translation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the{' '}
            {editingTranslation ? LANGUAGE_LABELS[editingTranslation.language] : ''} translation?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteTranslation} color="error" variant="contained" disabled={saving}>
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
